<template>
  <Modal
    :title="modalTitle"
    class-name="customer-equipment-modal"
    @hide-modal="$emit('hide-modal')"
    @submit="handleSubmit"
  >
    <EquipmentForm
      ref="equipmentForm"
      :always-show-address-select="alwaysShowAddressSelect"
      :customer-id="customerId"
      :equipment="equipment"
      :address="address"
    />
    <template #delete>
      <div v-if="isEditing">
        <SeraButton
          v-if="archiveLink"
          text="Archive"
          type="danger"
          @click="toggleArchived(archiveLink)"
        />
        <SeraButton
          v-if="unarchiveLink"
          text="Unarchive"
          outline
          @click="toggleArchived(unarchiveLink)"
        />
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { getProperty } from "@/lib/helpers.js";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import EquipmentForm from "@/Equipment/EquipmentForm.vue";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    Modal,
    SeraButton,
    EquipmentForm
  },
  props: {
    alwaysShowAddressSelect: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: Number,
      default: null
    },
    equipmentId: {
      type: Number,
      default: null
    },
    address: {
      type: Object,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  data() {
    return {
      equipment: null
    };
  },
  computed: {
    ...mapState({
      currentUser: "currentUser",
      currentUserAddresses: "currentUserAddresses",
      modalTitle() {
        return this.equipment ? `Edit ${this.equipment.name}` : "Add Equipment";
      },
      isEditing() {
        return Boolean(this.equipmentId);
      },
      updateLink() {
        return getProperty(this.equipment, "links.edit");
      },
      archiveLink() {
        return getProperty(this.equipment, "links.archive");
      },
      unarchiveLink() {
        return getProperty(this.equipment, "links.unarchive");
      },
      equipmentTypeLabel() {
        return getProperty(this.selectedEquipmentType, "name") || "--";
      }
    })
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  mounted() {
    this.loadCustomer();
    this.loadEquipment();
  },
  methods: {
    ...mapActions([
      "fetchEquipment",
      "createEquipment",
      "updateEquipment",
      "fetchUser"
    ]),
    async handleSubmit() {
      const data = await this.$refs.equipmentForm.getRequestData();
      if (data) {
        this.saving = true;
        let equipment = null;
        try {
          if (this.isEditing) {
            await this.updateEquipment({
              customer: this.currentUser,
              link: this.updateLink,
              data
            });
            window.notify.success("Equipment updated sucessfully!");
          } else {
            const address = this.currentUserAddresses.find(
              (address) => address.id === data.address_id
            );
            equipment = await this.createEquipment({
              link: address.links.equipment_create,
              data
            });
            window.notify.success("Equipment created sucessfully!");
          }
          this.$emit("hide-modal");
          this.$emit("save-success", equipment);
        } catch (e) {
          console.error(e);
        }
        this.saving = false;
      }
    },
    async toggleArchived(link) {
      try {
        // The link includes the archived flag param
        await this.updateEquipment({
          customer: this.currentUser,
          link,
          data: null
        });
        window.notify.success("Equipment updated successfully!");
        this.$emit("hide-modal");
        this.$emit("save-success");
      } catch (e) {
        // do-nothing
      }
    },
    async loadEquipment() {
      if (this.equipmentId && this.customerId) {
        try {
          this.equipment = await this.fetchEquipment({
            customerId: this.customerId,
            equipmentId: this.equipmentId
          });
        } catch (e) {
          console.error(e);
        }
      }
    },
    loadCustomer() {
      if (!this.currentUser) {
        this.fetchUser({ id: this.customerId });
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
